/**
 * B8 — KPI budgets & checks (owner rulings 2026-10-25, docs/beta-features.md).
 *
 * BUDGETS are PER STORE, PER MONTH (his ruling: each store has its own
 * budget — "how many rentals they are expected to do"): one StoreBudgets row
 * per store per year holds every KPI's 12 monthly values as JSON, keyed by
 * the template family (AuditType) and the KPI question id (ids never change
 * across versions):
 *
 *   { "v": 1, "t": { "<templateKey>": { "<kpiId>": [jan, …, dec] } } }   (null = no budget)
 *
 * KPI SETTINGS are company-wide, per template (AuditTemplates.KpiSettings,
 * the same on every version, Admin-set): which way is good ("higher is
 * better" / "lower is better") and how a value compares with the month's
 * budget — a MONTH-TO-DATE COUNT (his ruling: counts like Move-ins are typed
 * month to date) is compared with the budget pro-rated to the visit's day;
 * a LEVEL (a percent, open work orders) is compared as it is.
 *
 *   { "v": 1, "q": { "<kpiId>": { "dir": "up" | "down", "kind": "mtd" | "level" } } }
 *
 * COLOURS (his ruling): green = meets the budget; amber = misses by up to 10%
 * (5 points for percents); red = misses by more. BIG CHANGE on the form
 * (his ruling): more than 50% (15 points for percents) away from the average
 * of the store's last 3 visits — warn only, never blocks submit.
 *
 * Pure; the app and the server's write checks share it (no browser imports).
 */

export type KpiType = 'number' | 'percent';
export type KpiDirection = 'up' | 'down';
/** 'mtd' = a month-to-date count (pro-rated budget); 'level' = compared as it is. */
export type KpiKind = 'mtd' | 'level';

export interface KpiSetting {
  dir: KpiDirection;
  kind: KpiKind;
}

/** templateKey → kpiId → 12 monthly budgets (null = none). */
export type BudgetMap = Record<string, Record<string, (number | null)[]>>;

export interface KpiSettingsMap {
  v: 1;
  q: Record<string, KpiSetting>;
}

/** Caps for a row written by a person or an import (a forged row can't flood a page). */
export const MAX_BUDGET_TEMPLATES = 20;
export const MAX_BUDGET_KPIS = 200;
export const MAX_BUDGET_VALUE = 1e9;
/** Amber band: a number may miss by up to 10%, a percent by up to 5 points. */
export const AMBER_FRACTION = 0.1;
export const AMBER_POINTS = 5;
/** Big change: more than 50% away (numbers) / 15 points (percents) from the last-3 average. */
export const BIG_CHANGE_FRACTION = 0.5;
export const BIG_CHANGE_POINTS = 15;
/** Month-to-date counts aren't checked before this day of the month (a pace from 1–4 days is noise). */
export const MTD_CHECK_FROM_DAY = 5;

const ID = /^[a-z0-9][a-z0-9_-]{0,79}$/i;

/** defineProperty: a hand-edited "__proto__" key must not replace the prototype. */
function put<T>(obj: Record<string, T>, key: string, value: T) {
  Object.defineProperty(obj, key, { value, enumerable: true, writable: true, configurable: true });
}
const own = (obj: object, key: string) => Object.prototype.hasOwnProperty.call(obj, key);

/** A budget value as stored: a finite number within ±MAX_BUDGET_VALUE, else null. */
export function budgetValue(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) && Math.abs(v) <= MAX_BUDGET_VALUE ? v : null;
}

/**
 * Read a StoreBudgets.Budgets value defensively: malformed parts are
 * dropped, never thrown. `strict` (the server's write check) throws instead,
 * so a bad row is refused rather than silently trimmed.
 */
export function parseBudgets(raw: unknown, strict = false): BudgetMap {
  const fail = (why: string): BudgetMap => {
    if (strict) throw new Error(why);
    return {};
  };
  if (raw === null || raw === undefined || raw === '') return {};
  let parsed: unknown = raw;
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return fail('Budgets must be JSON.');
    }
  }
  if (!parsed || typeof parsed !== 'object' || (parsed as { v?: unknown }).v !== 1) return fail('Budgets must have v: 1.');
  const t = (parsed as { t?: unknown }).t;
  if (t === undefined) return {};
  if (!t || typeof t !== 'object' || Array.isArray(t)) return fail('Budgets "t" must be an object.');
  const out: BudgetMap = {};
  const templates = Object.entries(t as Record<string, unknown>);
  if (strict && templates.length > MAX_BUDGET_TEMPLATES) return fail('Too many templates in one budget row.');
  for (const [key, kpis] of templates.slice(0, MAX_BUDGET_TEMPLATES)) {
    if (!ID.test(key) || !kpis || typeof kpis !== 'object' || Array.isArray(kpis)) {
      if (strict) return fail(`Bad template key "${key.slice(0, 40)}".`);
      continue;
    }
    const entries = Object.entries(kpis as Record<string, unknown>);
    if (strict && entries.length > MAX_BUDGET_KPIS) return fail('Too many KPIs in one budget row.');
    const row: Record<string, (number | null)[]> = {};
    for (const [kpi, months] of entries.slice(0, MAX_BUDGET_KPIS)) {
      if (!ID.test(kpi) || !Array.isArray(months) || months.length !== 12) {
        if (strict) return fail(`Bad KPI "${kpi.slice(0, 40)}": 12 monthly values expected.`);
        continue;
      }
      const values = months.map((m) => (m === null ? null : budgetValue(m)));
      if (strict && values.some((v, i) => v === null && months[i] !== null)) return fail(`Bad value in KPI "${kpi}".`);
      put(row, kpi, values);
    }
    put(out, key, row);
  }
  return out;
}

/** JSON for StoreBudgets.Budgets (KPIs with no value in any month are left out). */
export function serializeBudgets(map: BudgetMap): string {
  const t: BudgetMap = {};
  for (const [key, kpis] of Object.entries(map)) {
    const row: Record<string, (number | null)[]> = {};
    for (const [kpi, months] of Object.entries(kpis)) {
      if (months.some((m) => m !== null)) put(row, kpi, months.map((m) => budgetValue(m)));
    }
    if (Object.keys(row).length > 0) put(t, key, row);
  }
  return JSON.stringify({ v: 1, t });
}

/** Read AuditTemplates.KpiSettings defensively. */
export function parseKpiSettings(raw: unknown): KpiSettingsMap {
  const out: KpiSettingsMap = { v: 1, q: {} };
  if (typeof raw !== 'string' || raw === '') return out;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return out;
  }
  const q = parsed && typeof parsed === 'object' ? (parsed as { q?: unknown }).q : undefined;
  if (!q || typeof q !== 'object' || Array.isArray(q)) return out;
  for (const [id, s] of Object.entries(q as Record<string, unknown>).slice(0, MAX_BUDGET_KPIS)) {
    if (!ID.test(id) || !s || typeof s !== 'object') continue;
    const dir = (s as { dir?: unknown }).dir;
    const kind = (s as { kind?: unknown }).kind;
    put(out.q, id, { dir: dir === 'down' ? 'down' : 'up', kind: kind === 'mtd' ? 'mtd' : 'level' });
  }
  return out;
}

export function serializeKpiSettings(map: KpiSettingsMap): string {
  return JSON.stringify({ v: 1, q: map.q });
}

/**
 * A KPI's setting, or its default when an admin hasn't set one: higher is
 * better; percents are levels; numbers are month-to-date counts (his ruling
 * — e.g. Move-ins). "Unavailable units"-style numbers need "level" + "lower
 * is better" set once in Budgets & goals.
 */
export function settingFor(map: KpiSettingsMap | null | undefined, kpiId: string, type: KpiType): KpiSetting {
  const s = map && own(map.q, kpiId) ? map.q[kpiId] : undefined;
  if (s) return type === 'percent' ? { dir: s.dir, kind: 'level' } : s;
  return { dir: 'up', kind: type === 'percent' ? 'level' : 'mtd' };
}

/**
 * What a KPI's target is called (owner ruling 2026-10-25, Reports redesign:
 * "not every KPI would be classified as a budget"): a month-to-date COUNT
 * (move-ins, rentals) has a **Budget**; a LEVEL (occupancy %, AR 30+ %) has
 * a **Goal**. Headings, hints and reports use the word; storage is unchanged.
 */
export function kpiWord(setting: Pick<KpiSetting, 'kind'>, lower = false): string {
  const w = setting.kind === 'mtd' ? 'Budget' : 'Goal';
  return lower ? w.toLowerCase() : w;
}

/** The month's budget (0 = January) for one store's KPI, or null. */
export function monthBudget(map: BudgetMap | null | undefined, templateKey: string, kpiId: string, month: number): number | null {
  if (!map || !own(map, templateKey)) return null;
  const kpis = map[templateKey]!;
  if (!own(kpis, kpiId)) return null;
  return budgetValue(kpis[kpiId]![month]);
}

/** Calendar parts of an ISO day "yyyy-mm-dd". */
export function dayParts(isoDay: string): { year: number; month: number; day: number; daysInMonth: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDay);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]) - 1;
  const day = Number(m[3]);
  if (month < 0 || month > 11 || day < 1) return null;
  const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  if (day > daysInMonth) return null;
  return { year, month, day, daysInMonth };
}

/**
 * What the value should be by `isoDay` (Eastern calendar day of the visit):
 * a month-to-date count's budget pro-rated to the day of the month; a level
 * as it is. null = no budget.
 */
export function expectedBy(budget: number | null, setting: KpiSetting, isoDay: string): number | null {
  if (budget === null) return null;
  if (setting.kind === 'level') return budget;
  const p = dayParts(isoDay);
  if (!p) return null;
  return (budget * p.day) / p.daysInMonth;
}

export type BudgetBand = 'green' | 'amber' | 'red';

/**
 * Meets the budget? green = meets; amber = misses by up to 10% (numbers) or
 * 5 points (percents); red = misses by more. A number budget of 0 (or less)
 * can't be missed "by a percentage": lower-is-better misses it at any value
 * above it (red); higher-is-better always meets it.
 */
export function budgetBand(value: number, expected: number, type: KpiType, dir: KpiDirection): BudgetBand {
  const miss = dir === 'up' ? expected - value : value - expected;
  if (miss <= 0) return 'green';
  if (type === 'percent') return miss <= AMBER_POINTS ? 'amber' : 'red';
  if (expected <= 0) return dir === 'up' ? 'green' : 'red';
  return miss / expected <= AMBER_FRACTION ? 'amber' : 'red';
}

/**
 * A month-to-date count as a full-month pace (value ÷ share of the month
 * gone); levels as they are. null when it's too early in the month to say.
 */
export function paceOf(value: number, setting: KpiSetting, isoDay: string): number | null {
  if (setting.kind === 'level') return value;
  const p = dayParts(isoDay);
  if (!p || p.day < MTD_CHECK_FROM_DAY) return null;
  return (value * p.daysInMonth) / p.day;
}

export interface PastValue {
  /** Eastern calendar day of the visit. */
  day: string;
  value: number;
}

export interface BigChange {
  /** The average the new value is compared with (a pace for month-to-date counts). */
  average: number;
  /** The new value (a pace for month-to-date counts). */
  now: number;
}

/**
 * The "Big change — double-check" check (warn only): the new value against
 * the average of the store's last 3 visits (newest first in `history`).
 * Month-to-date counts compare full-month paces, so a visit on the 28th
 * isn't "bigger" than one on the 6th. null = no warning (or can't tell).
 */
export function bigChange(value: number, isoDay: string, history: readonly PastValue[], type: KpiType, setting: KpiSetting): BigChange | null {
  const now = paceOf(value, setting, isoDay);
  if (now === null) return null;
  const past = history
    .slice(0, 3)
    .map((h) => paceOf(h.value, setting, h.day))
    .filter((v): v is number => v !== null);
  if (past.length === 0) return null;
  const average = past.reduce((s, v) => s + v, 0) / past.length;
  const gap = Math.abs(now - average);
  const limit = type === 'percent' ? BIG_CHANGE_POINTS : Math.max(BIG_CHANGE_FRACTION * Math.abs(average), 1);
  return gap > limit ? { average, now } : null;
}

/** "Oct" for month 9. */
export const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;
